import { NgModule } from '@angular/core';
import { ExtraOptions, Routes, RouterModule, mapToCanActivate } from '@angular/router';
import { HybridAuthGuard } from '@edassist/api';
import { OnDemandPreloadStrategy, constants } from '@edassist/common';
import { WelcomePageGuard } from '@tuition/app/modules/welcome-page/welcome-page-guard';

export const routingConfiguration: ExtraOptions = {
	paramsInheritanceStrategy: 'always',
	preloadingStrategy: OnDemandPreloadStrategy,
	scrollPositionRestoration: 'top'
};

const routes: Routes = [
	{
		path: 'welcome',
		loadChildren: () => import('./modules/welcome-page/welcome-page.module').then((module) => module.WelcomePageModule),
		canActivate: mapToCanActivate([WelcomePageGuard]),
		data: {
			showNavBar: false,
			title: constants.pageTitles.welcome
		}
	},
	{
		path: 'survey',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/survey/survey.module').then((module) => module.SurveyModule),
		data: {
			preload: true,
			title: constants.pageTitles.survey,
			showNavBar: false
		}
	},
	{
		path: 'home',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/tuition-home/tuition-home.module').then((module) => module.TuitionHomeModule),
		data: { preload: true, title: constants.pageTitles.home }
	},
	{
		path: 'home/:activeSlide',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/tuition-home/tuition-home.module').then((module) => module.TuitionHomeModule),
		data: { preload: true, title: constants.pageTitles.home }
	},
	{
		path: 'fast-track/learn-more/:participantId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/fast-track-learn-more/fast-track-learn-more.module').then(
				(module) => module.FastTrackLearnMoreModule
			),
		data: { preload: true }
	},
	{
		path: 'application-document/:documentId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('@edassist/common/components/application-document-view-base/application-document-view-base.module').then(
				(module) => module.BaseApplicationDocumentViewModule
			),
		data: { preload: true }
	},
	{
		path: 'review-applications',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/review-applications/review-applications.module').then(
				(module) => module.ReviewApplicationsModule
			),
		data: { preload: true, title: constants.pageTitles.reviewApplications }
	},
	{
		path: 'notifications',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('@edassist/common/components/notifications/view/notifications-view.module').then(
				(module) => module.NotificationsViewModule
			),
		data: { preload: true, title: constants.pageTitles.NOTIFICATIONS }
	},
	{
		path: 'notifications/:participantId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('@edassist/common/components/notifications/view/notifications-view.module').then(
				(module) => module.NotificationsViewModule
			),
		data: { preload: true, title: constants.pageTitles.NOTIFICATIONS }
	},
	{
		path: 'advising',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/advising/advising.module').then((module) => module.AdvisingModule),
		data: { preload: true, title: constants.pageTitles.EDUCATION_COACHING }
	},
	{
		path: 'advising-appointment',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/advising-appointment/advising-appointment.module').then(
				(module) => module.AdvisingAppointmentModule
			),
		data: { preload: true, title: constants.pageTitles.ADVISING_APPOINTMENT }
	},
	{
		path: 'advising-appointment/:appointmentId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/advising-appointment/advising-appointment.module').then(
				(module) => module.AdvisingAppointmentModule
			),
		data: { preload: true, title: constants.pageTitles.ADVISING_APPOINTMENT }
	},
	{
		path: 'sso-logout',
		loadChildren: () =>
			import('@edassist/common/components/sso-logout/sso-logout.module').then((module) => module.SsoLogoutModule),
		data: { preload: true, title: constants.pageTitles.LOGOUT }
	},
	{
		path: 'advising-submission-confirmation',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/advising-submission-confirmation/advising-submission-confirmation.module').then(
				(module) => module.AdvisingSubmissionConfirmationModule
			),
		data: {
			preload: true,
			title: constants.pageTitles.ADVISING_SUBMISSION_CONFIRMATION
		}
	},
	{
		path: 'career-pathways',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/tuition-career-pathways/career-pathways.module').then((module) => module.CareerPathwaysModule),
		data: { preload: true, title: constants.pageTitles.CAREER_PATHWAYS }
	},
	{
		path: 'career-pathways/:careerPathwayId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/tuition-career-pathways/career-pathways.module').then((module) => module.CareerPathwaysModule),
		data: { preload: true, title: constants.pageTitles.CAREER_PATHWAYS }
	},
	{
		path: 'site-map',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('@edassist/common/components/site-map-link/site-map-link.module').then(
				(module) => module.SiteMapLinkModule
			),
		data: { preload: true, title: constants.pageTitles.SITE_MAP }
	},
	{
		path: 'workforce-education/learn-more/:participantId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/workforce-education-network/workforce-education-network.module').then(
				(module) => module.WorkforceEducationNetworkModule
			),
		data: { preload: true }
	},
	{
		path: 'jumpstart/learn-more',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/jump-start/jump-start.module').then((module) => module.JumpStartModule),
		data: { preload: true }
	},
	{
		path: 'client-custom-pathway',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/client-custom-pathway/client-custom-pathway.module').then(
				(module) => module.ClientCustomPathwayModule
			),
		data: { preload: true, title: constants.pageTitles.CLIENT_CUSTOM_PATHWAY }
	},
	{
		path: 'profile/:participantId',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/tuition-profile/tuition-profile.module').then((module) => module.TuitionProfileModule),
		data: { preload: true, title: constants.pageTitles.participantProfile }
	},
	{
		path: 'support',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () =>
			import('./modules/tuition-support/tuition-support.module').then((module) => module.TuitionSupportModule),
		data: { preload: true, title: constants.pageTitles.participantProfile }
	},
	{
		path: 'marketplace',
		canActivate: mapToCanActivate([HybridAuthGuard]),
		loadChildren: () => import('./modules/tuition-home/tuition-home.module').then((module) => module.TuitionHomeModule),
		data: { preload: true, title: constants.pageTitles.EDUCATION_MARKET_PLACE }
	},
	{
		path: '**',
		redirectTo: 'home'
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes, routingConfiguration)],
	exports: [RouterModule]
})
export class AppRoutingModule {}
